/**
 * A shared chapter's text as the read view shows it (part 38c; decision 75: read only): each visible block (a paragraph,
 * heading or centered line, decision 41's visible text) as runs of the same marks (italic, bold, small caps), each run
 * with its offset in the chapter's visible text, the same offsets a note's anchor holds (decision 76, part 19e's AN1).
 * So a selection is a visible range as it is, and a note's passage is marked where its range says. Pure; the text is
 * shown as text, never as markup (decision 59).
 */
import { ChapterPlaces, chapterModel, parseChapter, visibleBlocks } from '@cockatrice/core';
import type { Block, Inline } from '@cockatrice/core';

/** A run's marks. */
export interface Marks {
  readonly emphasis: boolean;
  readonly strong: boolean;
  readonly smallCaps: boolean;
}

/** A run of text with the same marks, at `from` in the chapter's visible text. */
export interface Run {
  readonly text: string;
  readonly from: number;
  readonly marks: Marks;
}

/** A block shown: its kind (a heading with its level), its offset in the visible text, and its runs. */
export interface ShownBlock {
  readonly kind: 'paragraph' | 'heading' | 'centered';
  readonly level: number;
  readonly from: number;
  readonly runs: readonly Run[];
}

/**
 * A chapter shown: its blocks, its visible text (the blocks joined by line ends, AN1), and its places (to make a note's
 * passage), made only when asked for (a chapter switch needs none: the bench's budget).
 */
export interface ShownChapter {
  readonly blocks: readonly ShownBlock[];
  readonly visible: string;
  readonly places: () => ChapterPlaces;
}

/** A styled span of the source: its kind and its source range. */
interface Styled {
  readonly kind: 'emphasis' | 'strong' | 'small-caps';
  readonly from: number;
  readonly to: number;
}

function inlineStyles(content: readonly Inline[], out: Styled[]): void {
  for (const item of content) {
    if (item.kind === 'emphasis' || item.kind === 'strong' || item.kind === 'small-caps') out.push({ kind: item.kind, from: item.from, to: item.to });
    // Text, breaks and references hold no marks; spans, links and pictures' words and brackets may hold some.
    if (item.kind === 'emphasis' || item.kind === 'strong' || item.kind === 'small-caps' || item.kind === 'link' || item.kind === 'image') inlineStyles(item.children, out);
    else if (item.kind === 'brackets') inlineStyles(item.text, out);
  }
}

/** Every styled span and every heading's level (by the heading's source start) in `blocks`, quotes' and lists' too. */
function walk(blocks: readonly Block[], styles: Styled[], levels: Map<number, number>): void {
  for (const block of blocks) {
    if (block.kind === 'heading') levels.set(block.from, block.level);
    // Scene breaks hold no text; a footnote's definition shows as its own visible block, its marks as a paragraph's.
    if (block.kind === 'heading' || block.kind === 'paragraph' || block.kind === 'centered') inlineStyles(block.content, styles);
    else if (block.kind === 'quote' || block.kind === 'footnote') walk(block.blocks, styles, levels);
    else if (block.kind === 'list') for (const item of block.items) walk(item, styles, levels);
  }
}

const BITS: Readonly<Record<Styled['kind'], number>> = { emphasis: 1, strong: 2, 'small-caps': 4 };

/** The marks of `active` (the styled spans covering a point) as bits: italic 1, bold 2, small caps 4. */
function maskOf(active: readonly Styled[]): number {
  let mask = 0;
  for (const style of active) mask |= BITS[style.kind];
  return mask;
}

/** The marks `mask` holds. */
function marksOf(mask: number): Marks {
  return { emphasis: (mask & 1) !== 0, strong: (mask & 2) !== 0, smallCaps: (mask & 4) !== 0 };
}

/** The chapter whose source is `source`, shown: its visible blocks as runs, and its places. */
export function shownChapter(source: string): ShownChapter {
  const parsed = parseChapter(source);
  const styles: Styled[] = [];
  const levels = new Map<number, number>();
  walk(chapterModel(parsed).blocks, styles, levels);
  styles.sort((a, b) => a.from - b.from);
  const blocks: ShownBlock[] = [];
  const texts: string[] = [];
  let at = 0;
  // A sweep over the source: spans' starts never decrease within a block, and the visible blocks follow the source
  // (part 04's table V; the property test holds it against a scan per unit): the spans begun so far, less those ended.
  let next = 0;
  let active: Styled[] = [];
  for (const block of visibleBlocks(parsed)) {
    const runs: Run[] = [];
    let start = 0;
    let mask = -1;
    for (let unit = 0; unit < block.text.length; unit += 1) {
      const point = Number(block.spans[2 * unit]);
      for (let style = styles[next]; style !== undefined && style.from <= point; style = styles[next]) {
        active.push(style);
        next += 1;
      }
      if (active.some((style) => style.to <= point)) active = active.filter((style) => style.to > point);
      const now = maskOf(active);
      // A run ends where the marks change; each is cut from the block's text once (no growing strings).
      if (now !== mask && unit > 0) runs.push({ text: block.text.slice(start, unit), from: at + start, marks: marksOf(mask) });
      if (now !== mask) {
        start = unit;
        mask = now;
      }
    }
    if (block.text.length > 0) runs.push({ text: block.text.slice(start), from: at + start, marks: marksOf(mask) });
    blocks.push({ kind: block.kind, level: levels.get(block.from) ?? 0, from: at, runs });
    at += block.text.length + 1;
    texts.push(block.text);
  }
  let places: ChapterPlaces | null = null;
  return {
    blocks,
    visible: texts.join('\n'),
    places: () => (places ??= new ChapterPlaces(parsed)),
  };
}

/**
 * NV: `runs` cut where any of `ranges` (visible ranges: notes' passages) start or end, each piece saying which ranges
 * cover it (their indices), so a passage is marked over whatever marks its text has.
 */
export function cutRuns(runs: readonly Run[], ranges: readonly { readonly from: number; readonly to: number }[]): { readonly run: Run; readonly covered: readonly number[] }[] {
  const out: { run: Run; covered: number[] }[] = [];
  // Only the ranges that reach these runs are looked at for each (a chapter's block meets few of its notes).
  const first = runs[0]?.from ?? 0;
  const last = runs.reduce((end, run) => Math.max(end, run.from + run.text.length), first);
  const near = ranges.flatMap((range, which) => (range.from < last && range.to > first ? [{ range, which }] : []));
  for (const run of runs) {
    const end = run.from + run.text.length;
    const cuts = new Set<number>([run.from, end]);
    for (const { range } of near) {
      if (range.from > run.from && range.from < end) cuts.add(range.from);
      if (range.to > run.from && range.to < end) cuts.add(range.to);
    }
    const points = [...cuts].sort((a, b) => a - b);
    for (let index = 0; index + 1 < points.length; index += 1) {
      const from = Number(points[index]);
      const to = Number(points[index + 1]);
      const covered = near.flatMap(({ range, which }) => (range.from <= from && to <= range.to && range.from < range.to ? [which] : []));
      out.push({ run: { text: run.text.slice(from - run.from, to - run.from), from, marks: run.marks }, covered });
    }
  }
  return out;
}
